import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminUsersTab = ({ users, updateUser }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [customProfiles, setCustomProfiles] = useState('');
  const { confirm } = useNotification();
  const { t } = useLanguage();

  const handleToggleActive = async (user) => {
    if (user.is_active) {
      // Deactivating locks the user out immediately — reactivating is
      // reversible, so only the destructive direction needs a confirmation.
      const ok = await confirm(
        t('adminUsers.confirm.deactivate', `¿Desactivar a ${user.email}? Perderá acceso inmediato a su cuenta.`).replace('{email}', user.email),
        { title: t('adminUsers.deactivateUser', 'Desactivar usuario'), confirmLabel: t('adminUsers.deactivate', 'Desactivar') }
      );
      if (!ok) return;
    }
    updateUser(user.id, { is_active: !user.is_active });
  };

  const handleProfilesChange = (userId, value) => {
    if (value === 'custom') {
      setEditingUser(userId);
      setCustomProfiles('');
    } else {
      updateUser(userId, { max_profiles: parseInt(value) });
    }
  };

  const handleCustomProfilesSave = (userId) => {
    const profiles = parseInt(customProfiles);
    if (profiles > 0) {
      updateUser(userId, { max_profiles: profiles });
      setEditingUser(null);
      setCustomProfiles('');
    }
  };

  return (
    <div className="bg-white card-shadow-lg rounded-xl p-8">
      <h3 className="text-lg font-semibold text-black mb-6">{t('adminUsers.title', 'Gestión de Usuarios')}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.user', 'Usuario')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.status', 'Estado')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.company', 'Empresa')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.profiles', 'Perfiles')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('adminUsers.actions', 'Acciones')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? t('adminUsers.active', 'Activo') : t('adminUsers.inactive', 'Inactivo')}
                  </span>
                  {user.activation_used && (
                    <span className="ml-2 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {t('adminUsers.activated', 'Activado')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 max-w-xs">
                  {user.team_name ? (
                    <div title={user.company_description || ''}>
                      <div className="text-sm font-medium text-gray-900 truncate">{user.team_name}</div>
                      {user.industry && (
                        <span className="mt-1 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          {user.industry}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">{t('adminUsers.notFilledOut', 'Sin completar')}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.profiles_count} / {user.max_profiles}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleToggleActive(user)}
                    className={`${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                  >
                    {user.is_active ? t('adminUsers.deactivate', 'Desactivar') : t('adminUsers.activate', 'Activar')}
                  </button>

                  {editingUser === user.id ? (
                    <div className="flex items-center space-x-2 mt-2">
                      <input
                        type="number"
                        min="1"
                        value={customProfiles}
                        onChange={(e) => setCustomProfiles(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 w-20"
                        placeholder={t('adminUsers.placeholderExample', 'Ej: 5')}
                      />
                      <button
                        onClick={() => handleCustomProfilesSave(user.id)}
                        className="text-green-600 hover:text-green-900 text-sm"
                      >
                        {t('adminUsers.active', 'Activo')}
                      </button>
                      <button
                        onClick={() => setEditingUser(null)}
                        className="text-gray-600 hover:text-gray-900 text-sm"
                      >
                        {t('adminUsers.inactive', 'Inactivo')}
                      </button>
                    </div>
                  ) : (
                    <select
                      value={user.max_profiles}
                      onChange={(e) => handleProfilesChange(user.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      {[1, 2, 3, 4, 5, 10, 15, 20, 30].map(num => (
                        <option key={num} value={num}>{t('adminUsers.numProfiles', `${num} perfiles`).replace('{n}', num)}</option>
                      ))}
                      <option value="custom">{t('adminUsers.custom', 'Personalizado...')}</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default AdminUsersTab;
